import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../hooks/useLocale';
import usePageMeta from '../hooks/usePageMeta';
import { tField } from '../utils/i18nContent';
import {
  TTF_ORDER_DEADLINE,
  computeTtfOrderAmounts,
  isValidClabe,
  isTtfTransfer,
  formatTtfMxn,
  TTF_CALCULATOR_ONLY,
} from '../utils/ttf';
import {
  fetchTtfCatalog,
  createTtfOrder,
  createTtfPieceRequest,
} from '../services/ttfService';
import TtfCountdown from '../components/TtfCountdown';
import TtfPriceCalculator from '../components/TtfPriceCalculator';
import { trackTtfStep, TTF_STEPS } from '../services/analyticsService';
import './TtfOrderPage.css';

// Bold points in the statement shown below the calculator (es/ttf.json).
const STATEMENT_POINTS = ['point1', 'point2', 'point3', 'point4'];

// Label for the amount due at order time, per payment method.
const DUE_NOW_LABELS = {
  mercado_pago: 'cart.dueNowMercadoPago',
  bank_transfer_full: 'cart.dueNowTransferFull',
};

const emptyCustomer = { name: '', instagram: '', whatsapp: '' };
const emptyRequest = { artistName: '', referenceUrl: '', notes: '', wantsSignature: false, customerName: '', instagram: '', whatsapp: '' };

// Instagram user input with a fixed "@" in front; strips an "@" the
// customer types themselves so it isn't doubled.
const InstagramInput = ({ id, value, onChange }) => (
  <div className="ttf-input-prefixed">
    <span className="ttf-input-prefix" aria-hidden="true">@</span>
    <input
      id={id}
      className="ttf-input"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/^@+/, ''))}
      required
      autoCapitalize="none"
      autoCorrect="off"
      spellCheck={false}
      maxLength={30}
    />
  </div>
);

const Field = ({ id, label, hint, children }) => (
  <div className="ttf-field">
    <label htmlFor={id} className="ttf-label">{label}</label>
    {children}
    {hint && <span className="ttf-hint">{hint}</span>}
  </div>
);

// `calculatorOnly` defaults to the TTF_CALCULATOR_ONLY switch; tests pass it
// explicitly to exercise both versions of the page.
const TtfOrderPage = ({ calculatorOnly = TTF_CALCULATOR_ONLY }) => {
  const { t } = useTranslation('ttf');
  const locale = useLocale();
  const money = (amount) => formatTtfMxn(amount, locale);

  usePageMeta({ title: t('meta.title'), description: t('meta.description') });

  const [pieces, setPieces] = useState([]);
  const [deadline, setDeadline] = useState(TTF_ORDER_DEADLINE);
  const [isOpen, setIsOpen] = useState(() => Date.now() < new Date(TTF_ORDER_DEADLINE).getTime());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // pieceId → { wantsSignature } — one of each piece per customer
  const [selection, setSelection] = useState({});
  const [customer, setCustomer] = useState(emptyCustomer);
  const [deliveryMethod, setDeliveryMethod] = useState('pickup');
  const [paymentMethod, setPaymentMethod] = useState('mercado_pago');
  const [clabe, setClabe] = useState('');
  const [clabeHolder, setClabeHolder] = useState('');
  const [clabeBank, setClabeBank] = useState('');
  const [clabeTouched, setClabeTouched] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // 'order' → 'done'
  const [step, setStep] = useState('order');
  const [order, setOrder] = useState(null);

  const [request, setRequest] = useState(emptyRequest);
  const [requestSending, setRequestSending] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [requestSent, setRequestSent] = useState(false);

  useEffect(() => { trackTtfStep(TTF_STEPS.view); }, []);

  useEffect(() => {
    if (calculatorOnly) return undefined; // nothing from the catalog is shown
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchTtfCatalog();
        if (cancelled) return;
        setPieces(data.pieces || []);
        if (data.deadline) setDeadline(data.deadline);
        if (data.orderingOpen === false) setIsOpen(false);
      } catch (err) {
        if (!cancelled) setLoadError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [calculatorOnly]);

  const handleClosed = useCallback(() => setIsOpen(false), []);

  const piecesById = useMemo(() => new Map(pieces.map((p) => [p.id, p])), [pieces]);

  const lines = useMemo(
    () => Object.entries(selection)
      .map(([id, sel]) => ({ piece: piecesById.get(Number(id)), ...sel }))
      .filter((line) => line.piece),
    [selection, piecesById]
  );

  const amounts = computeTtfOrderAmounts(
    lines.map((l) => ({ price: l.piece.price_mxn, quantity: 1, is_lottery: l.piece.is_lottery })),
    deliveryMethod,
    paymentMethod
  );

  const addPiece = (piece) => {
    if (selection[piece.id]) return;
    setSelection((prev) => ({ ...prev, [piece.id]: { wantsSignature: false } }));
    trackTtfStep(TTF_STEPS.addPiece, { pieceId: piece.id, pieceName: piece.name, isLottery: Boolean(piece.is_lottery) });
  };

  const updateLine = (pieceId, changes) =>
    setSelection((prev) => ({ ...prev, [pieceId]: { ...prev[pieceId], ...changes } }));

  const removeLine = (pieceId) => {
    setSelection((prev) => {
      const next = { ...prev };
      delete next[pieceId];
      return next;
    });
    trackTtfStep(TTF_STEPS.removePiece, { pieceId });
  };

  const setCustomerField = (field) => (e) => setCustomer((prev) => ({ ...prev, [field]: e.target.value }));

  const clabeDigits = clabe.replace(/\D/g, '');
  // Wrong length shows once the customer leaves the field (or types too
  // many digits); a bad check digit shows as soon as there are 18.
  const clabeError = !isTtfTransfer(paymentMethod) || clabeDigits.length === 0
    ? null
    : clabeDigits.length === 18
      ? (isValidClabe(clabeDigits) ? null : 'invalid')
      : (clabeTouched || clabeDigits.length > 18 ? 'length' : null);
  const clabeLooksWrong = clabeError !== null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitting(true);
    const stepData = {
      paymentMethod,
      deliveryMethod,
      pieceIds: lines.map((l) => l.piece.id),
      signatures: lines.filter((l) => l.wantsSignature).length,
      dueNow: amounts.dueNow,
    };
    trackTtfStep(TTF_STEPS.checkoutSubmit, stepData);
    try {
      const result = await createTtfOrder({
        items: lines.map((l) => ({ pieceId: l.piece.id, wantsSignature: l.wantsSignature })),
        customer,
        deliveryMethod,
        paymentMethod,
        clabe: isTtfTransfer(paymentMethod) ? clabeDigits : undefined,
        clabeHolder: isTtfTransfer(paymentMethod) ? clabeHolder : undefined,
        clabeBank: isTtfTransfer(paymentMethod) ? clabeBank : undefined,
        acceptTerms,
      });
      setOrder(result);
      trackTtfStep(TTF_STEPS.orderCreated, { ...stepData, orderCode: result.orderCode, dueNow: result.amounts?.dueNow });
      setStep('done');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message);
      trackTtfStep(TTF_STEPS.orderFailed, { ...stepData, error: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const resetOrder = () => {
    setSelection({});
    setOrder(null);
    setAcceptTerms(false);
    setStep('order');
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setRequestError(null);
    setRequestSending(true);
    try {
      await createTtfPieceRequest(request);
      trackTtfStep(TTF_STEPS.requestSubmitted, { wantsSignature: request.wantsSignature, hasLink: Boolean(request.referenceUrl) });
      setRequestSent(true);
      setRequest(emptyRequest);
    } catch (err) {
      setRequestError(err.message);
    } finally {
      setRequestSending(false);
    }
  };

  const setRequestField = (field) => (e) => setRequest((prev) => ({ ...prev, [field]: e.target.value }));

  // ---------------------------------------------------------------------------
  // Confirmation screen
  // ---------------------------------------------------------------------------
  const renderDone = () => {
    const isTransfer = isTtfTransfer(order.paymentMethod) && order.amounts.dueNow > 0;
    return (
      <section className="ttf-panel ttf-panel--narrow ttf-success">
        <h2 className="ttf-section-title">{t('success.title')}</h2>
        {order.orderCode && <p className="ttf-order-code">{t('success.orderCode', { code: order.orderCode })}</p>}
        {isTransfer ? (
          <>
            <p>{t('success.transferIntroFull', { amount: money(order.amounts.dueNow) })}</p>
            <dl className="ttf-bank">
              {order.bank?.bankName && (<><dt>{t('success.bankName')}</dt><dd>{order.bank.bankName}</dd></>)}
              {order.bank?.beneficiary && (<><dt>{t('success.beneficiary')}</dt><dd>{order.bank.beneficiary}</dd></>)}
              {order.bank?.clabe && (<><dt>{t('success.clabe')}</dt><dd>{order.bank.clabe}</dd></>)}
            </dl>
            <p>{t('success.reference')}</p>
            <p>{t('success.sendProof')}</p>
          </>
        ) : order.paymentMethod === 'mercado_pago' && order.amounts.dueNow > 0 ? (
          <p>{t('success.mercadoPago', { amount: money(order.amounts.dueNow) })}</p>
        ) : (
          <p>{t('success.lotteryOnly')}</p>
        )}
        {isOpen && (
          <button type="button" className="ttf-button ttf-button--secondary" onClick={resetOrder}>
            {t('success.another')}
          </button>
        )}
      </section>
    );
  };

  // ---------------------------------------------------------------------------
  // Page
  // ---------------------------------------------------------------------------
  if (calculatorOnly) {
    return (
      <div className="ttf-page">
        <header className="ttf-hero">
          <h1 className="ttf-title">{t('hero.title')}</h1>
        </header>
        <TtfPriceCalculator />
        <section className="ttf-panel ttf-panel--narrow ttf-statement" aria-labelledby="ttf-statement-title" lang="es">
          <h2 id="ttf-statement-title" className="ttf-section-title">{t('statement.title')}</h2>
          <p>{t('statement.intro')}</p>
          <p>{t('statement.thanks')}</p>
          <ul className="ttf-list">
            {STATEMENT_POINTS.map((key) => <li key={key}>{t(`statement.${key}`)}</li>)}
          </ul>
          <p className="ttf-statement-closing">
            {t('statement.closing')}
            <br />
            <span className="ttf-statement-signature">{t('statement.signature')}</span>
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="ttf-page">
      <header className="ttf-hero">
        <h1 className="ttf-title">{t('hero.title')}</h1>
      </header>

      {step === 'done' && renderDone()}

      {step === 'order' && (
        <>
          <div className="ttf-layout">
            <section className="ttf-catalog">
              <h2 className="ttf-section-title">{t('catalog.title')}</h2>
              {loading && <p className="ttf-muted">{t('catalog.loading')}</p>}
              {loadError && <p className="ttf-error" role="alert">{loadError}</p>}
              {!loading && !loadError && pieces.length === 0 && <p className="ttf-muted">{t('catalog.empty')}</p>}
              <div className="ttf-grid">
                {pieces.map((piece) => {
                  const name = tField(piece, 'name', locale);
                  const inOrder = Boolean(selection[piece.id]);
                  // Unpriced pieces are listed without an add button until priced.
                  const hasPrice = piece.price_mxn != null;
                  return (
                    <article key={piece.id} className={`ttf-card${piece.is_sold_out ? ' is-sold-out' : ''}`}>
                      <div className="ttf-card-figure">
                        {piece.image_url && <img src={piece.image_url} alt={name} loading="lazy" />}
                        <div className="ttf-badges">
                          {piece.is_lottery && <span className="ttf-badge ttf-badge--lottery">{t('catalog.lottery')}</span>}
                          {piece.is_obtained && !piece.is_sold_out && <span className="ttf-badge ttf-badge--obtained">{t('catalog.obtained')}</span>}
                          {piece.is_sold_out && <span className="ttf-badge ttf-badge--sold-out">{t('catalog.soldOut')}</span>}
                        </div>
                      </div>
                      <h3 className="ttf-card-name" title={name}>{name}</h3>
                      {piece.artist_name && <p className="ttf-card-artist">{piece.artist_name}</p>}
                      {tField(piece, 'description', locale) && (
                        <p className="ttf-card-description" title={tField(piece, 'description', locale)}>{tField(piece, 'description', locale)}</p>
                      )}
                      <p className={`ttf-card-price${hasPrice ? '' : ' ttf-card-price--tbd'}`}>
                        {hasPrice ? money(Number(piece.price_mxn)) : t('catalog.priceTbd')}
                      </p>
                      {hasPrice && (
                        <button
                          type="button"
                          className={`ttf-button${inOrder ? ' ttf-button--secondary' : ''}`}
                          onClick={() => addPiece(piece)}
                          disabled={!isOpen || piece.is_sold_out || inOrder}
                        >
                          {piece.is_sold_out ? t('catalog.soldOut') : inOrder ? t('catalog.added') : t('catalog.add')}
                        </button>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>

            <aside className="ttf-order">
              <form onSubmit={handleSubmit} className="ttf-panel">
                <h2 className="ttf-section-title">{t('cart.title')}</h2>
                {lines.length > 0 && (
                  <ul className="ttf-lines">
                    {lines.map(({ piece, wantsSignature }) => (
                      <li key={piece.id} className="ttf-line">
                        <div className="ttf-line-head">
                          <span className="ttf-line-name">{tField(piece, 'name', locale)}</span>
                          <span>{money(Number(piece.price_mxn))}</span>
                        </div>
                        {piece.is_lottery && <span className="ttf-hint">{t('cart.lotteryNote')}</span>}
                        <div className="ttf-line-controls">
                          <button type="button" className="ttf-link-button" onClick={() => removeLine(piece.id)}>
                            {t('cart.remove')}
                          </button>
                        </div>
                        <label className="ttf-inline">
                          <input
                            type="checkbox"
                            checked={wantsSignature}
                            onChange={(e) => updateLine(piece.id, { wantsSignature: e.target.checked })}
                          />
                          {t('cart.wantsSignature')}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}

                <h3 className="ttf-subtitle">{t('form.title')}</h3>
                <Field id="ttf-name" label={t('form.name')}>
                  <input id="ttf-name" className="ttf-input" value={customer.name} onChange={setCustomerField('name')} required autoComplete="name" />
                </Field>
                <Field id="ttf-instagram" label={t('form.instagram')}>
                  <InstagramInput id="ttf-instagram" value={customer.instagram} onChange={(instagram) => setCustomer((prev) => ({ ...prev, instagram }))} />
                </Field>
                <Field id="ttf-whatsapp" label={t('form.whatsapp')} hint={t('form.whatsappHint')}>
                  <input id="ttf-whatsapp" type="tel" className="ttf-input" value={customer.whatsapp} onChange={setCustomerField('whatsapp')} required autoComplete="tel" />
                </Field>

                <fieldset className="ttf-fieldset">
                  <legend className="ttf-label">{t('form.delivery')}</legend>
                  {[['pickup', t('form.pickup')], ['delivery', t('form.shipping')]].map(([value, label]) => (
                    <label key={value} className="ttf-inline">
                      <input type="radio" name="ttf-delivery" value={value} checked={deliveryMethod === value} onChange={() => setDeliveryMethod(value)} />
                      {label}
                    </label>
                  ))}
                </fieldset>

                <fieldset className="ttf-fieldset">
                  <legend className="ttf-label">{t('form.payment')}</legend>
                  {[['mercado_pago', t('form.mercadoPago')], ['bank_transfer_full', t('form.transferFull')]].map(([value, label]) => (
                    <label key={value} className="ttf-inline">
                      <input type="radio" name="ttf-payment" value={value} checked={paymentMethod === value} onChange={() => setPaymentMethod(value)} />
                      {label}
                    </label>
                  ))}
                </fieldset>
                {isTtfTransfer(paymentMethod) && (
                  <>
                    <Field id="ttf-clabe" label={t('form.clabe')} hint={clabeLooksWrong ? null : t('form.clabeHint')}>
                      <input
                        id="ttf-clabe"
                        inputMode="numeric"
                        className="ttf-input"
                        value={clabe}
                        onChange={(e) => setClabe(e.target.value)}
                        onBlur={() => setClabeTouched(true)}
                        required
                        maxLength={24}
                      />
                    </Field>
                    {clabeError === 'invalid' && <p className="ttf-error">{t('form.clabeInvalid')}</p>}
                    {clabeError === 'length' && <p className="ttf-error">{t('form.clabeLength', { count: clabeDigits.length })}</p>}
                    <Field id="ttf-clabe-bank" label={t('form.clabeBank')}>
                      <input id="ttf-clabe-bank" className="ttf-input" value={clabeBank} onChange={(e) => setClabeBank(e.target.value)} required />
                    </Field>
                    <Field id="ttf-clabe-holder" label={t('form.clabeHolder')}>
                      <input id="ttf-clabe-holder" className="ttf-input" value={clabeHolder} onChange={(e) => setClabeHolder(e.target.value)} required />
                    </Field>
                  </>
                )}

                {lines.length > 0 && (
                  <dl className="ttf-totals">
                    <dt>{t('cart.subtotal')}</dt><dd>{money(amounts.subtotal - amounts.lotterySubtotal)}</dd>
                    {amounts.lotterySubtotal > 0 && (<><dt>{t('cart.lotterySubtotal')}</dt><dd>{money(amounts.lotterySubtotal)}</dd></>)}
                    {amounts.deliveryFee > 0 && (<><dt>{t('cart.delivery')}</dt><dd>{money(amounts.deliveryFee)}</dd></>)}
                    <dt>{t('cart.total')}</dt><dd>{money(amounts.total)}</dd>
                    <dt className="ttf-totals-strong">{t(DUE_NOW_LABELS[paymentMethod])}</dt>
                    <dd className="ttf-totals-strong" data-testid="ttf-due-now">{money(amounts.dueNow)}</dd>
                    {amounts.dueLater > 0 && (<><dt>{t('cart.dueLater')}</dt><dd>{money(amounts.dueLater)}</dd></>)}
                  </dl>
                )}

                <label className="ttf-inline ttf-terms">
                  <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
                  {t('form.terms')}
                </label>

                {submitError && <p className="ttf-error" role="alert">{submitError}</p>}

                <button
                  type="submit"
                  className="ttf-button ttf-button--block"
                  disabled={!isOpen || submitting || lines.length === 0 || !acceptTerms || clabeLooksWrong}
                >
                  {submitting ? t('form.submitting') : t('form.submit')}
                </button>
              </form>
            </aside>
          </div>

          <TtfPriceCalculator />

          <div className="ttf-countdown-wrap">
            <TtfCountdown deadline={deadline} onClose={handleClosed} />
          </div>

          <section className="ttf-panel ttf-info">
            <h2 className="ttf-section-title">{t('conditions.title')}</h2>
            <ul className="ttf-list">
              {['intro', 'mercadoPago', 'transferFull', 'fullPayment', 'lottery', 'refunds', 'pickup', 'shipping', 'abandoned'].map((key) => (
                <li key={key}>{t(`conditions.${key}`)}</li>
              ))}
            </ul>

            <h3 className="ttf-subtitle">{t('pricing.title')}</h3>
            <p>{t('pricing.intro')}</p>
            <ul className="ttf-list">
              <li>{t('pricing.fee')}</li>
              <li>{t('pricing.cashWithdrawal')}</li>
              <li>{t('pricing.transport')}</li>
              <li>{t('pricing.taxes')}</li>
            </ul>
            <p>{t('pricing.approx')}</p>
          </section>

          {isOpen && (
            <section className="ttf-panel ttf-panel--narrow ttf-request">
              <h2 className="ttf-section-title">{t('request.title')}</h2>
              <p>{t('request.intro')}</p>
              {requestSent ? (
                <p className="ttf-success-note" role="status">{t('request.success')}</p>
              ) : (
                <form onSubmit={handleRequestSubmit}>
                  {/* The piece */}
                  <div className="ttf-row ttf-row--2">
                    <Field id="ttf-req-artist" label={t('request.artistName')}>
                      <input id="ttf-req-artist" className="ttf-input" value={request.artistName} onChange={setRequestField('artistName')} />
                    </Field>
                    <Field id="ttf-req-url" label={t('request.referenceUrl')}>
                      <input id="ttf-req-url" type="url" className="ttf-input" value={request.referenceUrl} onChange={setRequestField('referenceUrl')} />
                    </Field>
                  </div>
                  <Field id="ttf-req-notes" label={t('request.notes')}>
                    <textarea id="ttf-req-notes" className="ttf-input" rows={3} value={request.notes} onChange={setRequestField('notes')} />
                  </Field>
                  <label className="ttf-inline">
                    <input
                      type="checkbox"
                      checked={request.wantsSignature}
                      onChange={(e) => setRequest((prev) => ({ ...prev, wantsSignature: e.target.checked }))}
                    />
                    {t('request.wantsSignature')}
                  </label>

                  {/* The customer — name, Instagram and WhatsApp on one row */}
                  <div className="ttf-row ttf-row--3 ttf-request-contact">
                    <Field id="ttf-req-name" label={t('form.name')}>
                      <input id="ttf-req-name" className="ttf-input" value={request.customerName} onChange={setRequestField('customerName')} required />
                    </Field>
                    <Field id="ttf-req-instagram" label={t('form.instagram')}>
                      <InstagramInput id="ttf-req-instagram" value={request.instagram} onChange={(instagram) => setRequest((prev) => ({ ...prev, instagram }))} />
                    </Field>
                    <Field id="ttf-req-whatsapp" label={t('form.whatsapp')}>
                      <input id="ttf-req-whatsapp" type="tel" className="ttf-input" value={request.whatsapp} onChange={setRequestField('whatsapp')} required />
                    </Field>
                  </div>

                  {requestError && <p className="ttf-error" role="alert">{requestError}</p>}
                  <div className="ttf-actions">
                    <button type="submit" className="ttf-button" disabled={requestSending}>
                      {requestSending ? t('request.submitting') : t('request.submit')}
                    </button>
                  </div>
                </form>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default TtfOrderPage;
